import userManagementSvc from "../../services/api/userManagement";
import { useEffect, useState } from "react";
import { notify } from "../../common/notificationService";
import { updateUserSchema } from "../../models/schema/auth";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormInput from "../../components/form-input/FormInput";
import { convertObjectToFormData } from "../../utils/helper";
import { MediaUrl, getUserInfo, listGender } from "../../utils/configuration";
import FormSelect from "../../components/form-input/formSelect";
import FormDate from "../../components/form-input/formDate";
import dayjs from "dayjs";
import { LoaderCircle } from "lucide-react";
import FormTextarea from "../../components/form-input/FormTextarea";
import commonSvc from "../../services/api/common";
import DialogComponent from "../../components/dialog";
import DialogChangePassword from "./DialogChangePassword";
import { useNavigate } from "react-router-dom";
import { UrlCollection } from "../../common/url-collection";
import { useUserInfo } from "../../hooks/useUserInfo";

const UserInfo = () => {
    const [idFileUpload, setIdFileUpload] = useState<number | null>(null);
    const [isChangePassword, setIsChangePassword] = useState<boolean>(false);
    const navigate = useNavigate()
    const { register, reset, handleSubmit, control, formState: { errors, isDirty } } = useForm({
        resolver: yupResolver(updateUserSchema),
    });
    const { userInfo, isUse2FA, setIsUse2FA, loading, setLoading, fetchUserInfo } = useUserInfo()
    const [previewAvatar, setPreviewAvatar] = useState<string | null>(null);

    const onSubmit = async (data: any) => {
        const formData = convertObjectToFormData(data);
        idFileUpload && formData.append("documentUploadId", String(idFileUpload));
        const res = await userManagementSvc.updateAccount(formData);
        if (res.content.status) {
            notify("Thành công", "Cập nhật thông tin thành công", "success");
            fetchUserInfo()
        }
    };

    const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                const formData = new FormData();
                formData.append("File", file);
                const res = await commonSvc.uploadFile(formData);
                if (res && res.content) {
                    setPreviewAvatar(res.content.path);
                    setIdFileUpload(res.content.id);
                }
            } catch (error) {
                console.log(error);
            }
        }
    }


    const handleChangeStatus2FA = async () => {
        setLoading(true);
        try {
            if (!getUserInfo()?.userId) return
            const res = await userManagementSvc.disable2FA(getUserInfo()!.userId);
            if (res && res.content.status) {
                setIsUse2FA(prev => !prev)
                notify("Thành công", "Vô hiệu hóa 2FA thành công", "success");
            }
        } catch (error) {
            notify("Lỗi", "Xảy ra lỗi", "danger");
        } finally {
            setLoading(false);
        }
    }


    useEffect(() => {
        if (userInfo) {
            reset({
                FullName: userInfo.fullName,
                FirstName: userInfo.firstName,
                LastName: userInfo.lastName,
                Email: userInfo.email,
                DateOfBirth: dayjs(userInfo.dateOfBirth).format("MM-DD-YYYY"),
                Sex: userInfo.sex,
                Hometown: userInfo.hometown,
                City: userInfo.city,
                Country: userInfo.country,
                PhoneNumber: userInfo.phoneNumber,
            });

            setPreviewAvatar(userInfo.avatarUrl)
        }
    }, [userInfo]);

    return (
        <>
            <div className="page-wrapper h-full">
                <div className="mx-auto p-8 bg-white rounded-lg shadow">
                    <div className="flex flex-col lg:flex-row gap-8">
                        <div className="flex flex-col items-center justify-start w-full lg:w-1/3">
                            <input
                                key={new Date().getDate()}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                id="avatar-upload"
                                onChange={handleUploadFile}
                            />
                            <div
                                className="w-40 h-40 rounded-full overflow-hidden border-4 border-gray-200 bg-gray-100 mb-4 cursor-pointer hover:ring-2 hover:ring-blue-400"
                                onClick={() => document.getElementById('avatar-upload')?.click()}
                                title="Click to change avatar"
                            >
                                {previewAvatar ? (
                                    <img src={MediaUrl + previewAvatar} alt="Avatar" className="object-cover w-full h-full" />
                                ) : (
                                    <div className="flex items-center justify-center w-full h-full text-gray-400">No Avatar</div>
                                )}
                            </div>

                            <div className="text-center">
                                <div className="font-semibold">{userInfo?.fullName}</div>
                                <div className="text-sm text-gray-500">{userInfo?.roles.map((role) => role.code).join(", ")}</div>
                            </div>
                            <div className="mt-4">
                                <div className="flex justify-center gap-1">
                                    <span className="text-[#383838] font-medium">Trạng thái xác thực 2 lớp:</span>
                                    <span className={isUse2FA ? "text-[#008000] font-medium" : "text-[#ff0000] font-medium"}>
                                        {isUse2FA ? "Đã bật" : "Chưa bật"}
                                    </span>
                                </div>
                                <span className="text-center">
                                    {isUse2FA ? <div className="flex justify-center items-center gap-1"><span className="text-[#ff0000] font-medium cursor-pointer" onClick={() => handleChangeStatus2FA()}>Tắt 2FA</span><span className="text-[#808080] font-medium">(Không khuyến nghị)</span></div> : <span>khuyến nghị <span className="text-[#008000] font-medium cursor-pointer" onClick={() => navigate(UrlCollection.Register2FA)}>Bật</span> xác thực 2 lớp để bảo mật tài khoản.</span>}
                                </span>
                            </div>
                        </div>
                        <div className="flex-1">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                                    <FormInput
                                        label="Full Name"
                                        name="FullName"
                                        register={register("FullName")}
                                        error={errors.FullName}
                                    />
                                    <FormInput
                                        label="First Name"
                                        name="FirstName"
                                        register={register("FirstName")}
                                        error={errors.FirstName}
                                    />
                                    <FormInput
                                        label="Last Name"
                                        name="LastName"
                                        register={register("LastName")}
                                        error={errors.LastName}
                                    />
                                    <FormInput
                                        label="Email"
                                        name="Email"
                                        type="email"
                                        register={register("Email")}
                                        error={errors.Email}
                                    />
                                    <FormDate
                                        label="Date of Birth"
                                        name="DateOfBirth"
                                        control={control}
                                        errors={errors}
                                    />
                                    <FormSelect
                                        label="Giới tính"
                                        options={listGender}
                                        name="Sex"
                                        control={control}
                                        errors={errors.Sex}
                                    />
                                    <FormInput
                                        label="Hometown"
                                        name="Hometown"
                                        register={register("Hometown")}
                                        error={errors.Hometown}
                                    />
                                    <FormInput
                                        label="City"
                                        name="City"
                                        register={register("City")}
                                        error={errors.City}
                                    />
                                    <FormInput
                                        label="Country"
                                        name="Country"
                                        register={register("Country")}
                                        error={errors.Country}
                                    />
                                    <FormInput
                                        label="Phone Number"
                                        name="PhoneNumber"
                                        register={register("PhoneNumber")}
                                        error={errors.PhoneNumber}
                                    />
                                    <FormTextarea
                                        label="Description"
                                        name="Description"
                                        register={register("Description")}
                                        error={errors.Description}
                                        rows={3}
                                    />
                                    <div className="flex flex-row items-end">
                                        <button
                                            type="button"
                                            className="mb-2 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            onClick={() => {
                                                setIsChangePassword(true);
                                            }}
                                        >
                                            Đổi mật khẩu
                                        </button>
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    className="mt-4 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!isDirty && !idFileUpload}
                                >
                                    {loading ? <LoaderCircle /> : ""}
                                    Cập nhật
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {isChangePassword && (
                <DialogComponent
                    isOpen={isChangePassword}
                    title="Đổi mật khẩu"
                    onClose={() => {
                        setIsChangePassword(false);
                    }}
                >
                    <DialogChangePassword handleCloseChangePassword={() => setIsChangePassword(false)} />
                </DialogComponent>
            )}
        </>
    );
}

export default UserInfo;